import React, { useState, useEffect } from "react";
import { ReactComponent as ChatIcon } from "../../icons/gnai-chat.svg"; // Import the SVG file
import { ReactComponent as Close } from "../../icons/close.svg"; // Import the SVG file
import { ReactComponent as SendChat } from "../../icons/chat-send.svg"; // Import the SVG file

import "./ChatComponent.css"; // Import the updated CSS file

interface Message {
  sender: "ai" | "user";
  content: string;
}

interface ChatComponentProps {
  messages: Message[];
  isLoading: boolean;
  onInput: (message: string) => void;
  onClear?: () => void;
  defaultMessages?: string[]; // New prop for default messages
}

const LoadingDots: React.FC = () => {
  const [dots, setDots] = useState<string>("");

  useEffect(() => {
    const interval = setInterval(() => {
      setDots((prev) => (prev.length >= 3 ? "" : prev + "."));
    }, 500); // Change every 500ms for a smooth animation

    return () => clearInterval(interval); // Cleanup on unmount
  }, []);

  return <div className="loading-dots">Thinking{dots}</div>;
};

const ChatComponent: React.FC<ChatComponentProps> = ({
  messages,
  isLoading,
  onInput,
  onClear,
  defaultMessages = [
    "What's the latest in AI?",
    "Tell me a fun fact!",
    "Give me some tech news.",
    "How can I learn TypeScript?",
  ], // Default value
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");

  const toggleChat = () => {
    setIsOpen(!isOpen);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
  };

  const handleSendMessage = () => {
    if (inputValue.trim()) {
      onInput(inputValue);
      setInputValue("");
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    setInputValue(suggestion);
  };

  return (
    <div className="chat-container">
      {isOpen && (
        <div className="chat-window">
          <div className="chat-header">
            <span>GNAi Chat</span>
            <button
              className="chat-clear-button"
              onClick={() => {
                onClear && onClear();
              }}
            >
              Clear
            </button>
          </div>
          <div className="chat-messages">
            {messages.length === 0 && !isLoading ? (
              <div className="suggestions">
                {defaultMessages.map((suggestion, index) => (
                  <div
                    key={index}
                    className="suggestion"
                    onClick={() => handleSuggestionClick(suggestion)}
                  >
                    {suggestion}
                  </div>
                ))}
              </div>
            ) : (
              <>
                {messages.map((message, index) => (
                  <div
                    key={index}
                    className={`chat-message ${
                      message.sender === "ai" ? "ai" : "user"
                    }`}
                    dangerouslySetInnerHTML={{
                      __html: formatMessage(message.content),
                    }}
                  ></div>
                ))}
                {isLoading && <LoadingDots />}
              </>
            )}
          </div>
          <div className="chat-input">
            <input
              type="text"
              placeholder="Type Your Message"
              value={inputValue}
              onChange={handleInputChange}
              onKeyPress={(event) => {
                if (event.key === "Enter") handleSendMessage();
              }}
            />
            <button className="send-button" onClick={handleSendMessage}>
              <SendChat />
            </button>
          </div>
        </div>
      )}
      <button className="chat-toggle-button" onClick={toggleChat}>
        {isOpen ? <Close width={44} /> : <ChatIcon width={44} />}
      </button>
    </div>
  );
};

export default ChatComponent;

function formatMessage(message: string): string {
  const lines = message.toString().split("\n");
  const formattedLines = lines.map((line, index) => {
    let formattedLine = line.replace(/\*\*(.*?)\*\*/g, "$1"); // Bold text between ** **
    formattedLine = formattedLine.replace(/^- /g, "<br>- "); // Bold bullet points
    if (index === 0) return formattedLine;
    return `<br>${formattedLine}`;
  });
  return formattedLines.join("");
}
